/**
 * Loadout arithmetic (08 §2.5, 02 §8, 04 §6): what is on the ranger and in each pod, what it
 * weighs and what it costs. Pure; the screen is a view of this. What the objective will miss is
 * in warnings.ts, the specialist gear in gear.ts.
 * Weights, prices and caps come from `GameApi.prices`; standard issue and the encumbrance
 * numbers are the simulation's own (src/sim/tuning.ts), so the screen cannot drift from play.
 */

import type { PriceList } from '../../../meta/api';
import { CARBINE, RANGER, STANDARD_ISSUE } from '../../../sim/tuning';
import type { IntelId, Inventory, Loadout, ModifierId } from '../../../sim/types';
import type { GearState } from './gear';
import { gearWeight, toGear } from './gear';

/** What can be packed: every `Inventory` count except the rounds already in the carbine. */
export const PACK_ITEMS = ['magazines', 'grenades', 'laws', 'bombs', 'firstAid'] as const;
export type PackItem = (typeof PACK_ITEMS)[number];
export type Contents = Record<PackItem, number>;

export interface LoadoutState {
  carried: Contents;
  pods: Contents[];
  /** Specialist gear on the ranger (gear.ts); absent where the operation's stock is not in play. */
  gear?: GearState;
}

/** -1 is the ranger; 0.. are pods A, B, C. */
export type ContainerIndex = number;
export const RANGER_CONTAINER: ContainerIndex = -1;
export const POD_LETTERS = ['A', 'B', 'C', 'D'] as const;

export function emptyContents(): Contents {
  return { magazines: 0, grenades: 0, laws: 0, bombs: 0, firstAid: 0 };
}

export function standardIssue(): Contents {
  const s = STANDARD_ISSUE;
  return { magazines: s.magazines, grenades: s.grenades, laws: s.laws, bombs: s.bombs, firstAid: s.firstAid };
}

/**
 * Pods on this mission: what the ranger's price list allows, less one under Two-Pod Limit, plus
 * the Pathfinder Team's extra pod (04 §8) - the same sum `GameApi.deploy` checks against.
 */
export function podCount(
  prices: PriceList,
  modifiers: readonly ModifierId[],
  intel: readonly IntelId[] = [],
): number {
  const base = Math.max(0, Math.round(prices.maxPods));
  const n = base - (modifiers.includes('two-pod-limit') ? 1 : 0) + (intel.includes('pathfinder-team') ? 1 : 0);
  return Math.min(POD_LETTERS.length, Math.max(0, n));
}

/** Standard issue on the ranger, every pod empty: free, and sometimes right (04 §6). */
export function initialLoadout(pods: number): LoadoutState {
  return { carried: standardIssue(), pods: Array.from({ length: pods }, emptyContents) };
}

export function contentsOf(state: LoadoutState, container: ContainerIndex): Contents | undefined {
  return container === RANGER_CONTAINER ? state.carried : state.pods[container];
}

export function weightOf(contents: Contents, prices: PriceList): number {
  let total = 0;
  for (const item of PACK_ITEMS) total += contents[item] * prices.weight[item];
  return total;
}

/** Everything on the ranger: his inventory and any specialist gear. */
export function carriedWeight(state: LoadoutState, prices: PriceList): number {
  return weightOf(state.carried, prices) + gearWeight(state.gear);
}

/** What `container` weighs now; the ranger's load includes his gear. */
export function containerWeight(state: LoadoutState, container: ContainerIndex, prices: PriceList): number {
  if (container === RANGER_CONTAINER) return carriedWeight(state, prices);
  const contents = state.pods[container];
  return contents === undefined ? 0 : weightOf(contents, prices);
}

export function capOf(container: ContainerIndex, prices: PriceList): number {
  return container === RANGER_CONTAINER ? prices.carryCap : prices.podCap;
}

/** Total of one item across the ranger and every pod. */
export function totalOf(state: LoadoutState, item: PackItem): number {
  return state.pods.reduce((sum, pod) => sum + pod[item], state.carried[item]);
}

/**
 * RP this loadout costs (04 §6): everything in a pod, and everything on the ranger beyond
 * standard issue. Leaving issue kit behind refunds nothing. Classic packs free.
 */
export function loadoutCost(state: LoadoutState, prices: PriceList, free: boolean): number {
  if (free) return 0;
  const issue = standardIssue();
  let cost = 0;
  for (const item of PACK_ITEMS) {
    const extra = Math.max(0, state.carried[item] - issue[item]);
    const podded = state.pods.reduce((sum, pod) => sum + pod[item], 0);
    cost += (extra + podded) * prices.price[item];
  }
  return cost;
}

/** RP one more of `item` in `container` would add (0 while refilling standard issue). */
export function nextUnitCost(
  state: LoadoutState,
  container: ContainerIndex,
  item: PackItem,
  prices: PriceList,
  free: boolean,
): number {
  if (free) return 0;
  if (container === RANGER_CONTAINER && state.carried[item] < standardIssue()[item]) return 0;
  return prices.price[item];
}

export type AddBlock = 'weight' | 'rp' | null;

/** Why one more cannot go in, or null when it can. Weight and RP are the only hard limits. */
export function addBlock(
  state: LoadoutState,
  container: ContainerIndex,
  item: PackItem,
  prices: PriceList,
  rpAvailable: number,
  free: boolean,
): AddBlock {
  const contents = contentsOf(state, container);
  if (contents === undefined) return 'weight';
  if (containerWeight(state, container, prices) + prices.weight[item] > capOf(container, prices)) return 'weight';
  const cost = loadoutCost(state, prices, free) + nextUnitCost(state, container, item, prices, free);
  return cost > rpAvailable ? 'rp' : null;
}

/** A new state with `delta` (+1 / -1) applied; the same state when the change is not allowed. */
export function adjust(
  state: LoadoutState,
  container: ContainerIndex,
  item: PackItem,
  delta: 1 | -1,
  prices: PriceList,
  rpAvailable: number,
  free: boolean,
): LoadoutState {
  const contents = contentsOf(state, container);
  if (contents === undefined) return state;
  if (delta === 1 && addBlock(state, container, item, prices, rpAvailable, free) !== null) return state;
  if (delta === -1 && contents[item] <= 0) return state;
  const changed: Contents = { ...contents, [item]: contents[item] + delta };
  return container === RANGER_CONTAINER
    ? { ...state, carried: changed }
    : { ...state, pods: state.pods.map((pod, i) => (i === container ? changed : pod)) };
}

function inventory(contents: Contents, roundsInMag: number): Inventory {
  return { ...contents, roundsInMag };
}

/** The sim's `Loadout`. Pod order is kept - an empty pod A must not turn pod B into pod A. */
export function toLoadout(state: LoadoutState): Loadout {
  const gear = toGear(state.gear);
  return {
    carried: inventory(state.carried, CARBINE.roundsPerMag),
    pods: state.pods.map((pod) => inventory(pod, 0)),
    ...(gear === undefined ? {} : { gear }),
  };
}

/** What the load does to the ranger on the ground (02 §4-5), as whole percentages. */
export function encumbrance(carriedWeight: number): { over: number; runSlower: number; tiresFaster: number } {
  const over = Math.max(0, carriedWeight - RANGER.freeWeight);
  return {
    over,
    runSlower: Math.round(over * RANGER.weightRunPenalty * 100),
    tiresFaster: Math.round(over * RANGER.weightFatiguePenalty * 100),
  };
}
